import { Component, Input, ViewChildren, ElementRef, OnInit, Output, EventEmitter } from "@angular/core";
import { FormBuilder, FormGroup, FormControlName, Validators } from "@angular/forms";
import { ToastrService } from 'ngx-toastr';

import { BsModalService, BsModalRef } from "ngx-bootstrap/modal";
import { Observable, merge, Subject, of, fromEvent } from 'rxjs';
import { debounceTime, switchMap, catchError } from 'rxjs/operators';

import { NgbDateParserFormatter, NgbDateNativeAdapter, NgbDateAdapter } from "@ng-bootstrap/ng-bootstrap";
import { NgbDateStringParserFormatter } from "../shared/formatters/ngbDateStringParserFormatter";
import { CustomValidators } from "../shared/validators/customValidators";

import { DiscardModalComponent } from '../shared/components/discard-modal.component';
import { ValidationMessages, ValidationRules } from '../shared/models/validationMessage';
import { GenericFormValidator } from '../shared/validators/genericFormArray.validator';

import { Job } from "./models/job";
import { JobService } from "./services/jobs.service";

@Component({
  selector: 'job-edit',
  templateUrl: './job-edit.component.html',
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateStringParserFormatter },
    { provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }
  ]
})
export class JobEditComponent implements OnInit {

  @ViewChildren(FormControlName, { read: ElementRef }) formInputElements: ElementRef[];


  @Input()
  mode: string = "new";

  @Input()
  job: Job;

  @Output()
  onSave: EventEmitter<Job> = new EventEmitter<Job>();

  @Output()
  cancel: EventEmitter<boolean> = new EventEmitter<boolean>();

  discardModal: BsModalRef;

  columns: Array<any> = [];

  isSaving: boolean = false;
  jobForm: FormGroup;
  displayMessage = new ValidationMessages();
  private validationRules = new ValidationRules();
  private genericValidator: GenericFormValidator;


  constructor(private fb: FormBuilder,
    private jobService: JobService,
    private bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private toastr: ToastrService) {


    this.validationRules.Messages = {
      title: {
        required: 'Job Title is required'
      },
      companyName: {
        required: 'Company Name is required'
      },
      jobUrl: {
        required: 'Job URL is required'
      }
    }
    this.columns.push({ value: "jobOpenings", text: "Openings" });
    this.columns.push({ value: "jobApplications", text: "Applications" });
    this.columns.push({ value: "jobInterviews", text: "Interviews" });
    this.columns.push({ value: "jobFollowups", text: "Follow Up" });
    this.columns.push({ value: "jobArchives", text: "Archive" });

    this.genericValidator = new GenericFormValidator(this.validationRules);
  }

  ngOnInit() {    

    if (this.job != null) {
      this.resetForm();
      this.setForm();
    } else {
      this.resetForm();
    }

  }

  ngAfterViewInit(): void {

    // Watch for the blur event from any input element on the form.

    let controlBlurs: Observable<any>[] = this.formInputElements
      .map((formControl: ElementRef) => fromEvent(formControl.nativeElement, 'blur'));

    // Merge the blur event observable with the valueChanges observable
    merge(this.jobForm.valueChanges, ...controlBlurs).pipe(
      debounceTime(500)
    ).subscribe(value => {
      this.displayMessage = this.genericValidator.processMessages(this.jobForm);
    });

  }

  setForm() {
    this.jobForm = this.fb.group({
      id: [this.job.id],
      title: [this.job.title, Validators.required],
      jobUrl: [this.job.jobUrl, Validators.required],
      companyName: [this.job.companyName],
      column: [this.job.column],
      address1: [this.job.address1],
      address2: [this.job.address2],
      city: [this.job.city],
      state: [this.job.state],
      zipcode: [this.job.zipcode],      
      postedDate: [this.job.postedDate == null ? null : new Date(this.job.postedDate), [CustomValidators.isDate]],
      applicationDate: [this.job.applicationDate == null ? null : new Date(this.job.applicationDate), [CustomValidators.isDate]],
      followUpDate: [this.job.followUpDate == null ? null : new Date(this.job.followUpDate), [CustomValidators.isDate]],
      interviewDate: [this.job.interviewDate == null ? null : new Date(this.job.interviewDate), [CustomValidators.isDate]],
      thankYouLetter: [this.job.thankYouLetter],
      thankYouEmail: [this.job.thankYouEmail],
      followUpLetter: [this.job.followUpLetter],
      rejected: [this.job.rejected],
      notes: [""]
    });
  }

  resetForm() {
    //Add form details
    this.jobForm = this.fb.group({
      id: [],
      title: ["", Validators.required],
      jobUrl: ["", Validators.required],
      column: ["jobOpenings"],
      companyName: [""],
      address1: [""],
      address2: [""],
      city: [""],
      state: [""],
      zipcode: [""],
      postedDate: [null, [CustomValidators.isDate]],
      applicationDate: [null, [CustomValidators.isDate]],
      followUpDate: [null, [CustomValidators.isDate]],
      interviewDate: [null, [CustomValidators.isDate]],
      thankYouLetter: [false],
      thankYouEmail: [false],
      followUpLetter: [false],
      rejected: [false],
      notes: [""]
    });
  }

  saveJob() {

    this.isSaving = true;
    this.genericValidator.touchAll(this.jobForm);

    this.displayMessage = this.genericValidator.processMessages(this.jobForm);

    console.log(this.jobForm)

    if (this.jobForm.valid) {      
              
      const job = Object.assign({} as Job, this.jobForm.value);

      if (job.postedDate == "") {
        job.postedDate = null;
      }

      if (job.applicationDate == "") {
        job.applicationDate = null;
      }

      if (job.interviewDate == "") {
        job.interviewDate = null;
      }

      if (job.followUpDate == "") {
        job.followUpDate = null;
      }

      if (job.archiveDate == "") {
        job.archiveDate = null;
      }

      if (job.id == null) {
        //add
        job.id = -1;

        this.jobService.add(job).subscribe(resp => {
          //resp has id for new object
          this.onSave.emit(resp);
          this.toastr.success("Job Saved");
          this.isSaving = false;

        }, error => {
            this.toastr.error("Error saving job");
            this.isSaving = false;
        });

      } else {
        //update
        this.jobService.update(job).subscribe(resp => {
          this.onSave.emit(job);
          this.toastr.success("Job Saved");
          this.isSaving = false;          
        }, error => {
            this.toastr.error("Error saving job");
            this.isSaving = false;            
        });
      }
      
    } else {
      this.toastr.warning("Errors on form fields. Check messages.");
      this.isSaving = false;
    }

  }

  cancelClick() {

    if (this.jobForm.dirty) {
      //confirm modal 
      this.discardModal = this.modalService.show(DiscardModalComponent);

      this.discardModal.content.onClose.subscribe((response: boolean) => {
        if (response) {
          this.cancel.emit(true);          
        }
      });
    } else {

      this.cancel.emit(true);          

      this.bsModalRef.hide();
    }

  }
}
